import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Subject, fromEvent } from 'rxjs';
import * as moment from 'moment';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';
import { MatMenuModule } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { ILocation } from 'src/app/features/customers/models/customers.models';
import { IInboxFilter } from '../../../../store/smart-inbox.reducers';
import { AppState } from 'src/app/core/app.state';
import { selectAccounts } from '../../../../store/smart-inbox.selectors';
import { IAccount } from '../../../../models/account.models';
import { ConversationClassification, ConversationCounterparty, ConversationStatus, SELECTABLE_COUNTERPARTIES } from '../../../../models/conversation.models';
import { CONVERSATION_CATEGORIES, CONVERSATION_STATUSES } from '../../../../data/conversation.data';
import { getUserInitials, getUserColor, getUserFullname } from '../../../../utils/user.util';
import {
  classificationLabelKey,
  counterpartyFilterVariant,
  counterpartyLabelKey,
} from '../../../../utils/classification.util';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { ITeamOption, IUser, ILabelOption } from 'src/app/features/settings/models/settings.models';
import { FetchLabelOptions } from 'src/app/features/settings/store/settings.actions';
import { IUser as AuthUser } from 'src/app/features/auth/authentication/models/auth-response';

/** Advanced filter values emitted on Apply: the store's filter minus the free-text search. */
export type IAdvancedFilter = Omit<IInboxFilter, 'searchTerm'>;

/**
 * Dropdown panel for advanced conversation filtering.
 * Rendered inside a <mat-menu> in the conversation-list search bar.
 */
@Component({
  selector: 'app-conversation-advanced-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatMenuModule,
    UiModule,
  ],
  templateUrl: './conversation-advanced-filter.component.html',
  styleUrls: ['./conversation-advanced-filter.component.scss'],
})
export class ConversationAdvancedFilterComponent implements OnInit, OnDestroy {
  @Input() currentFilter: IInboxFilter = {};
  @Output() applied = new EventEmitter<IAdvancedFilter>();
  @Output() cleared = new EventEmitter<void>();
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;

  readonly today = moment();

  // ── Accounts / Categories / Status / Assignee ───────────────────────────────

  accounts: IAccount[] = [];
  users: IUser[] = [];
  /**
   * From the `teamOptions` slice, filled by `FetchTeamOptions` -> GET /v1/teams/options.
   * NOT the `allTeams` slice: that one is filled from the settings route GET /v1/teams,
   * which 403s for anyone without `access:settings_team` — i.e. most inbox users.
   */
  teams: ITeamOption[] = [];
  currentUser: AuthUser | null = null;

  selectedAccountIds: number[] = [];
  selectedCategories: ConversationClassification[] = [];
  selectedCounterparties: ConversationCounterparty[] = [];
  selectedStatuses: ConversationStatus[] = [];
  selectedAssigneeIds: number[] = [];
  /** Single-select. `undefined`, never `0`, when no team is picked. */
  selectedTeamIds: number[] = [];

  /**
   * Only VISIBLE labels are offered. The server drops an id it cannot see and answers
   * UNFILTERED rather than empty, so offering a hidden one here would silently WIDEN
   * the list instead of narrowing it.
   */
  labels: ILabelOption[] = [];
  /**
   * In flight, as distinct from settled-and-empty. Without it a slow catalogue
   * reads as "No labels found", which is what a tenant with none looks like.
   */
  labelsLoading = false;
  selectedLabelIds: number[] = [];

  readonly conversationCategories = CONVERSATION_CATEGORIES;
  readonly conversationStatuses = CONVERSATION_STATUSES;
  readonly counterpartyOptions = SELECTABLE_COUNTERPARTIES;
  readonly classificationLabelKey = classificationLabelKey;
  readonly counterpartyLabelKey = counterpartyLabelKey;
  readonly counterpartyFilterVariant = counterpartyFilterVariant;
  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;
  readonly getUserFullname = getUserFullname;

  form = new FormGroup({
    from: new FormControl(''),
    to: new FormControl(''),
    subject: new FormControl(''),
    includesWords: new FormControl(''),
    excludesWords: new FormControl(''),
    dateFrom: new FormControl(''),
    dateTo: new FormControl(''),
    hasAttachment: new FormControl(false),
    isUrgent: new FormControl(false),
  });

  // ── Customer autocomplete ───────────────────────────────────────────────────

  customerSearchControl = new FormControl('', { nonNullable: true });
  customerLocations: ILocation[] = [];
  selectedCustomers: { id: number; displayName: string }[] = [];
  customerLoading = false;

  private customerPage = 1;
  private customerTotalPages = 1;
  private readonly customerPageSize = 5;
  private readonly scrollThreshold = 60;
  private destroy$ = new Subject<void>();
  private panelScrollDestroy$ = new Subject<void>();

  /** "From date" cannot be after "To date" (or today if "To date" is empty) */
  get dateFromMax(): moment.Moment {
    const to = this.form.controls.dateTo.value;
    return to ? moment(to) : this.today;
  }

  /** "To date" cannot be before "From date" */
  get dateToMin(): moment.Moment | null {
    const from = this.form.controls.dateFrom.value;
    return from ? moment(from) : null;
  }

  constructor(
    private customersService: CustomersService,
    private store: Store<AppState>,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.populateFromFilter();

    this.store.select(selectAccounts).pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => { this.accounts = accounts; });

    // Visible only: a hidden id is dropped server-side and answers UNFILTERED,
    // so offering one here would widen the list rather than narrow it.
    // The options list, not the admin one: inbox users without label
    // administration must still be able to filter by label.
    this.store.select((state) => state.settings.labelOptions).pipe(takeUntil(this.destroy$))
      .subscribe((labels) => { this.labels = (labels ?? []).filter((l) => l.visible); });

    this.store.select((state) => state.settings.labelOptionsLoading).pipe(takeUntil(this.destroy$))
      .subscribe((loading) => { this.labelsLoading = !!loading; });

    // Dispatched every open, so a label invented from the conversation picker
    // since the last one is offered without a reload.
    this.store.dispatch(FetchLabelOptions());

    this.store.select((state) => state.settings.allUsers).pipe(takeUntil(this.destroy$))
      .subscribe((users) => { this.users = users; });

    this.store.select((state) => state.settings.teamOptions ?? []).pipe(takeUntil(this.destroy$))
      .subscribe((teams) => { this.teams = teams; });

    this.customerSearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.loadCustomers(1, false, term.trim() || undefined));

    this.loadCustomers(1, false);
  }

  ngOnDestroy(): void {
    this.panelScrollDestroy$.next();
    this.panelScrollDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (!target.closest('.conversation-advanced-filter__customer-input')) {
      this.autoTrigger?.closePanel();
    }
  }

  // ── Accounts / Categories / Counterparty / Status / Assignee toggles ────────

  /** Toggle a value in or out, returning a new array. */
  private toggleIn<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  toggleAccount(id: number): void {
    this.selectedAccountIds = this.toggleIn(this.selectedAccountIds, id);
  }

  toggleCategory(type: ConversationClassification): void {
    this.selectedCategories = this.toggleIn(this.selectedCategories, type);
  }

  toggleCounterparty(type: ConversationCounterparty): void {
    this.selectedCounterparties = this.toggleIn(this.selectedCounterparties, type);
  }

  toggleStatus(type: ConversationStatus): void {
    this.selectedStatuses = this.toggleIn(this.selectedStatuses, type);
  }

  toggleAssignee(id: number): void {
    this.selectedAssigneeIds = this.toggleIn(this.selectedAssigneeIds, id);
  }

  /**
   * Same shape as `toggleAssignee`: toggling a selected team removes it, which is how
   * both the menu checkbox and the chip's close icon unset one. Immutable, because the
   * template and `onApply` both read the array.
   */
  toggleTeam(id: number): void {
    this.selectedTeamIds = this.selectedTeamIds.includes(id)
      ? this.selectedTeamIds.filter((t) => t !== id)
      : [...this.selectedTeamIds, id];
  }

  get isAssignedToMe(): boolean {
    return this.currentUser != null && this.selectedAssigneeIds.includes(this.currentUser.id);
  }

  toggleAssignedToMe(): void {
    if (this.currentUser == null) return;
    this.toggleAssignee(this.currentUser.id);
  }

  getUsersButCurrentUser(): IUser[] {
    return this.users.filter((user) => user.id !== this.currentUser?.id);
  }

  // ── Selected-item chip resolvers ─────────────────────────────────────────────

  get selectedAccounts(): IAccount[] {
    return this.accounts.filter((a) => this.selectedAccountIds.includes(a.id));
  }

  get selectedStatusItems(): { name: string; type: ConversationStatus; icon: string }[] {
    return this.conversationStatuses.filter((s) => this.selectedStatuses.includes(s.type));
  }

  get selectedAssigneeUsers(): IUser[] {
    return this.users.filter((u) => this.selectedAssigneeIds.includes(u.id));
  }

  get selectedLabels(): ILabelOption[] {
    return this.labels.filter((l) => this.selectedLabelIds.includes(l.id));
  }

  get selectedTeams(): ITeamOption[] {
    // Filters the OPTIONS, so an id the panel has not loaded yet yields no chip rather
    // than a blank one, and the chips render in the menu's own order.
    return this.teams.filter((t) => this.selectedTeamIds.includes(t.id));
  }

  toggleLabel(id: number): void {
    this.selectedLabelIds = this.selectedLabelIds.includes(id)
      ? this.selectedLabelIds.filter((l) => l !== id)
      : [...this.selectedLabelIds, id];
  }

  onApply(): void {
    const v = this.form.value;
    this.applied.emit({
      activeAccounts: this.selectedAccountIds.length
        ? this.accounts.filter((a) => this.selectedAccountIds.includes(a.id))
        : undefined,
      categories: this.selectedCategories.length ? [...this.selectedCategories] : undefined,
      counterparties: this.selectedCounterparties.length ? [...this.selectedCounterparties] : undefined,
      statuses: this.selectedStatuses.length ? [...this.selectedStatuses] : undefined,
      assigneeIds: this.selectedAssigneeIds.length ? [...this.selectedAssigneeIds] : undefined,
      teamIds: this.selectedTeamIds.length ? [...this.selectedTeamIds] : undefined,
      appliedLabelIds: this.selectedLabelIds.length
        ? [...this.selectedLabelIds]
        : undefined,
      from: v.from || undefined,
      to: v.to || undefined,
      subject: v.subject || undefined,
      includesWords: v.includesWords || undefined,
      excludesWords: v.excludesWords || undefined,
      dateFrom: v.dateFrom || undefined,
      dateTo: v.dateTo || undefined,
      hasAttachment: v.hasAttachment || undefined,
      isUrgent: v.isUrgent || undefined,
      customerIds: this.selectedCustomers.length
        ? this.selectedCustomers.map((c) => c.id)
        : undefined,
      selectedCustomers: this.selectedCustomers.length
        ? [...this.selectedCustomers]
        : undefined,
    });
  }

  onReset(): void {
    this.form.reset({
      from: '',
      to: '',
      subject: '',
      includesWords: '',
      excludesWords: '',
      dateFrom: '',
      dateTo: '',
      hasAttachment: false,
      isUrgent: false,
    });
    this.selectedCustomers = [];
    this.selectedAccountIds = [];
    this.selectedCategories = [];
    this.selectedCounterparties = [];
    this.selectedStatuses = [];
    this.selectedAssigneeIds = [];
    this.selectedTeamIds = [];
    this.selectedLabelIds = [];
    this.cleared.emit();
  }

  // ── Customer autocomplete helpers ───────────────────────────────────────────

  selectCustomer(location: ILocation): void {
    if (location.id == null) return;
    if (this.selectedCustomers.some((c) => c.id === location.id)) return;
    this.selectedCustomers.push({
      id: location.id,
      displayName: this.getLocationDisplayName(location),
    });
    this.customerSearchControl.setValue('');
  }

  removeCustomer(id: number): void {
    this.selectedCustomers = this.selectedCustomers.filter((c) => c.id !== id);
  }

  getLocationDisplayName(location: ILocation): string {
    const name = location.name ?? '';
    const company = location.company_name?.trim();
    return company ? `${company} – ${name}` : name;
  }

  onAutocompletePanelOpened(): void {
    // Tear down any previous scroll listener before attaching a new one
    this.panelScrollDestroy$.next();

    setTimeout(() => {
      const panel = this.autoTrigger?.autocomplete?.panel?.nativeElement as HTMLElement;
      if (!panel) return;

      fromEvent(panel, 'scroll')
        .pipe(takeUntil(this.panelScrollDestroy$), takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.customerLoading || this.customerPage >= this.customerTotalPages) return;
          const { scrollTop, scrollHeight, clientHeight } = panel;
          if (scrollTop + clientHeight >= scrollHeight - this.scrollThreshold) {
            this.loadCustomers(this.customerPage + 1, true, this.customerSearchControl.value.trim() || undefined);
          }
        });
    });
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private populateFromFilter(): void {
    this.form.patchValue({
      from: this.currentFilter.from ?? '',
      to: this.currentFilter.to ?? '',
      subject: this.currentFilter.subject ?? '',
      includesWords: this.currentFilter.includesWords ?? this.currentFilter.searchTerm ?? '',
      excludesWords: this.currentFilter.excludesWords ?? '',
      dateFrom: this.currentFilter.dateFrom ?? '',
      dateTo: this.currentFilter.dateTo ?? '',
      hasAttachment: this.currentFilter.hasAttachment ?? false,
      isUrgent: this.currentFilter.isUrgent ?? false,
    });

    if (this.currentFilter.selectedCustomers?.length) {
      this.selectedCustomers = [...this.currentFilter.selectedCustomers];
    }

    this.selectedAccountIds = (this.currentFilter.activeAccounts ?? []).map((a) => a.id);
    this.selectedCategories = [...(this.currentFilter.categories ?? [])];
    this.selectedCounterparties = [...(this.currentFilter.counterparties ?? [])];
    this.selectedStatuses = [...(this.currentFilter.statuses ?? [])];
    this.selectedAssigneeIds = [...(this.currentFilter.assigneeIds ?? [])];
    // Without this the panel reads "All" while the list is still narrowed by a team.
    this.selectedTeamIds = [...(this.currentFilter.teamIds ?? [])];
    this.selectedLabelIds = [...(this.currentFilter.appliedLabelIds ?? [])];
  }

  private loadCustomers(pageNum: number, append: boolean, searchKey?: string): void {
    if (this.customerLoading) return;
    this.customerLoading = true;

    this.customersService
      .listLocations(
        { number: pageNum, size: this.customerPageSize },
        undefined,
        searchKey ?? undefined,
        'name',
        'asc',
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const data = res.data ?? [];
          const meta = res.metadata;
          this.customerTotalPages = meta?.pages ?? 1;
          this.customerPage = pageNum;

          if (append) {
            const existingIds = new Set(this.customerLocations.map((l) => l.id));
            const newOnes = data.filter((l) => l.id != null && !existingIds.has(l.id));
            this.customerLocations = [...this.customerLocations, ...newOnes];
          } else {
            this.customerLocations = data;
          }
        },
        error: () => {
          if (!append) this.customerLocations = [];
        },
        complete: () => {
          this.customerLoading = false;
        },
      });
  }
}
